import type { TFunction, ActionItem, PatientInfo, Questionnaire } from '@/types'

interface DerivedInfo {
  state: string
  questionnaire_id?: number
  type?: string
}

/**
 * Builds the onboarding action list from patient API data.
 * Pure utility so it can be used in both initial fetch and refresh without duplication.
 */
export function buildActionList(patient: PatientInfo | null, t: TFunction): ActionItem[] {
  if (!patient || patient.user?.onBoardingComplete) return []

  // Derive questionnaire states from the unfinished-questionnaires list.
  // The backend only returns questionnaires that are NOT yet completed.
  const questionnaires: Questionnaire[] = Array.isArray(patient.questionnaires) ? patient.questionnaires : []
  const genQr: Questionnaire | undefined = questionnaires.find((q) => q.type === 'GEN')
  const medQr: Questionnaire | undefined = questionnaires.find((q) => q.type === 'IN_MIGR' || q.type === 'IN_CLUS')

  const derived: Record<string, unknown> = {
    ...patient,
    onboardGeneralQstrInfo: genQr
      ? { state: 'STARTED', questionnaire_id: genQr.id }
      : { state: 'FINISHED' },
    onboardMedicalQstrInfo: medQr
      ? { state: 'STARTED', questionnaire_id: medQr.id, type: medQr.type }
      : genQr
        ? { state: 'NULL' }      // GEN still in progress → medical not yet created
        : { state: 'FINISHED' }, // both completed
  }

  interface RelevantStep {
    key: string
    label: string
  }

  const relevantSteps: RelevantStep[] = [
    { key: 'identificationInfo', label: t('dashboard.label-identification', { ns: 'dashboard' }) },
    { key: 'insuranceInfo', label: t('dashboard.label-insurance', { ns: 'dashboard' }) },
    { key: 'onboardGeneralQstrInfo', label: t('dashboard.label-general', { ns: 'dashboard' }) },
    { key: 'onboardMedicalQstrInfo', label: t('dashboard.label-medical', { ns: 'dashboard' }) },
    { key: 'referrals', label: t('dashboard.label-referrals', { ns: 'dashboard' }) },
  ]

  const actions: ActionItem[] = []

  relevantSteps.forEach(({ key, label }: RelevantStep) => {
    const data = derived[key] as DerivedInfo | unknown[] | undefined
    if (!data) return

    if (key === 'referrals') {
      const state: string = Array.isArray(data) && data.length > 0 ? t('completed') : t('pending')
      actions.push({ key, label, state })
      return
    }

    actions.push({ key, label, ...(data as DerivedInfo) })
  })

  return actions
}
